'use client';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';

export interface ToolFaq {
  question: string;
  answer: string;
}

export interface ToolContentSectionProps {
  /** Eyebrow shown above the heading (e.g. "About this tool"). */
  eyebrow?: string;
  /** Heading for the descriptive content block. */
  heading: string;
  /** Two-paragraph body explaining what the tool does and how to read it. */
  introParagraphs: [string, string];
  /** Optional bullet list of features / how-it-works points. */
  bullets?: string[];
  /** FAQ heading. Defaults to "Frequently asked questions". */
  faqHeading?: string;
  /** FAQ items shown in an accordion. */
  faqs: ToolFaq[];
  /** Optional CTA text. Defaults to a sensible demo prompt. */
  ctaText?: string;
  /** Optional CTA link href. Defaults to `/demo`. */
  ctaHref?: string;
}

/**
 * Content block placed beneath a tool/calculator UI. Adds the surrounding
 * intro copy, feature bullets, and FAQ that calculators tend to lack —
 * improves word count, text/HTML ratio, and surfaces structured questions
 * that match SERP intent.
 */
export function ToolContentSection({
  eyebrow,
  heading,
  introParagraphs,
  bullets,
  faqHeading,
  faqs,
  ctaText,
  ctaHref = '/demo',
}: ToolContentSectionProps) {
  const { t, locale } = useTranslation();
  const isAr = locale === 'ar';
  const resolvedEyebrow =
    eyebrow ?? (isAr ? 'عن هذه الأداة' : 'About this tool');
  const resolvedFaqHeading =
    faqHeading ?? (isAr ? 'الأسئلة الشائعة' : 'Frequently asked questions');
  const resolvedCtaText =
    ctaText ??
    (isAr
      ? 'تريد اختصار الحساب؟ احجز ديمو ٣٠ دقيقة، ونحسب توفيرك بالضبط مباشرة مع فريقنا.'
      : 'Want to skip the math? Book a 30-minute demo and we’ll model your exact savings live with our team.');
  const bookDemoLabel = isAr ? 'احجز ديمو ←' : 'Book a demo →';
  return (
    <section className="border-t border-neutral-200 bg-white py-16 md:py-24">
      <div className="container mx-auto max-w-4xl px-4">
        <p className="mb-3 text-xs font-medium tracking-widest text-neutral-500 uppercase">
          {resolvedEyebrow}
        </p>
        <h2 className="font-serif text-3xl text-neutral-900 md:text-4xl">
          {heading}
        </h2>
        <div className="mt-6 space-y-4 text-base leading-relaxed text-neutral-700">
          <p>{introParagraphs[0]}</p>
          <p>{introParagraphs[1]}</p>
          {bullets && bullets.length > 0 && (
            <ul
              className={`${isAr ? 'mr-6' : 'ml-6'} list-disc space-y-2 text-neutral-700`}
            >
              {bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
          )}
        </div>

        <h2 className="mt-16 font-serif text-3xl text-neutral-900 md:text-4xl">
          {resolvedFaqHeading}
        </h2>
        <Accordion type="single" collapsible className="mt-6">
          {faqs.map((faq, idx) => (
            <AccordionItem key={faq.question} value={`item-${idx}`}>
              <AccordionTrigger
                className={`py-5 ${isAr ? 'text-right' : 'text-left'} text-base font-medium text-neutral-900`}
              >
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="pb-5 text-base leading-relaxed text-neutral-700">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <p className="mt-12 text-base leading-relaxed text-neutral-700">
          {resolvedCtaText}{' '}
          <Link
            href={ctaHref}
            aria-label={
              ctaHref === '/demo'
                ? t.common['Learn more about Open and book a demo']
                : undefined
            }
            className="font-medium text-neutral-900 underline underline-offset-4 decoration-neutral-400 hover:decoration-neutral-900"
          >
            {bookDemoLabel}
          </Link>
        </p>
      </div>
    </section>
  );
}
